import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LogOut, Wallet, History, User, Settings, ArrowUpCircle, RefreshCw, Crown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { formatCurrency } from '@/lib/utils'
import { DepositModal, WithdrawModal, HistoryModal } from '@/features/transactions'
import { NotificationBell } from '@/features/notifications'
import { useToast } from '@/hooks/use-toast'

type ModalType = 'deposit' | 'withdraw' | 'history' | null

export default function MainLayout() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toast } = useToast()
  const { user, logout, isDemo, reloadDemoBalance } = useAuth()
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [isReloading, setIsReloading] = useState(false)

  // Get the correct balance based on user type
  const currentBalance = isDemo ? (user?.demoBalance || 0) : (user?.balance || 0)

  const handleReloadDemoChips = async () => {
    setIsReloading(true)
    const success = await reloadDemoBalance()
    if (success) {
      toast({
        title: t('lobby.chipsReloaded', { amount: '10,000' }),
        variant: 'default',
      })
    }
    setIsReloading(false)
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  if (!user) {
    return null
  }

  const userInitials = user.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U'

  return (
    <div className="min-h-screen bg-casino-gradient flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-casino-black/90 backdrop-blur-sm border-b border-casino-gold/20">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/lobby')}
          >
            <img
              src="/img/logo.png"
              alt="Cosa Nostra Casino"
              className="w-12 h-12 object-contain"
            />
            <div className="hidden sm:block">
              <h1 className="font-cinzel text-casino-gold font-bold">COSA NOSTRA</h1>
              <p className="text-[10px] text-casino-gold/50 tracking-widest">CASINO</p>
            </div>
          </div>

          {/* Balance and User */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Quick action buttons */}
            {isDemo ? (
              <div className="hidden sm:flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReloadDemoChips}
                  disabled={isReloading}
                  className="border-casino-gold text-casino-gold hover:bg-casino-gold/10"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isReloading ? 'animate-spin' : ''}`} />
                  {t('lobby.reloadChips')}
                </Button>
              </div>
            ) : (
              <div className="hidden sm:flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveModal('deposit')}
                  className="border-green-600 text-green-500 hover:bg-green-900/20"
                >
                  <Wallet className="h-4 w-4 mr-1" />
                  {t('lobby.deposit')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveModal('withdraw')}
                  className="border-casino-gold text-casino-gold hover:bg-casino-gold/10"
                >
                  <ArrowUpCircle className="h-4 w-4 mr-1" />
                  {t('lobby.withdraw')}
                </Button>
              </div>
            )}

            {/* Balance */}
            <div className="text-right">
              <p className="text-xs text-gray-400">
                {isDemo ? t('lobby.demoChips') : t('lobby.balance')}
              </p>
              <p className="font-bold text-casino-gold text-lg">
                {formatCurrency(currentBalance)}
              </p>
            </div>

            {/* Notification Bell - only for real users */}
            {!isDemo && <NotificationBell />}

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar>
                    <AvatarFallback>{userInitials}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user.displayName || t('lobby.guest')}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isDemo ? (
                  <>
                    <DropdownMenuItem onClick={handleReloadDemoChips} disabled={isReloading}>
                      <RefreshCw className={`mr-2 h-4 w-4 ${isReloading ? 'animate-spin' : ''}`} />
                      {t('lobby.reloadChips')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="mr-2 h-4 w-4" />
                      {t('lobby.profile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile')} className="text-casino-gold">
                      <Crown className="mr-2 h-4 w-4" />
                      {t('lobby.becomeMember')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => setActiveModal('deposit')}>
                      <Wallet className="mr-2 h-4 w-4" />
                      {t('lobby.deposit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveModal('withdraw')}>
                      <ArrowUpCircle className="mr-2 h-4 w-4" />
                      {t('lobby.withdraw')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveModal('history')}>
                      <History className="mr-2 h-4 w-4" />
                      {t('lobby.history')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="mr-2 h-4 w-4" />
                      {t('lobby.profile')}
                    </DropdownMenuItem>
                    {user.isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Settings className="mr-2 h-4 w-4" />
                        {t('lobby.admin')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-500 text-sm border-t border-casino-gold/10">
        <p>{t('landing.over18')}</p>
        <p className="mt-1 text-xs">© 2024 Cosa Nostra Casino.</p>
      </footer>

      {/* Transaction Modals */}
      <DepositModal
        isOpen={activeModal === 'deposit'}
        onClose={() => setActiveModal(null)}
      />
      <WithdrawModal
        isOpen={activeModal === 'withdraw'}
        onClose={() => setActiveModal(null)}
      />
      <HistoryModal
        isOpen={activeModal === 'history'}
        onClose={() => setActiveModal(null)}
      />
    </div>
  )
}
